/**
 * @file StatementSyncPanel.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-19
 * @description "Sync with Xero" for statements, on steps 1 and 2 of Generate
 *              Statements: a button, when it last ran, and — while it runs —
 *              a loading bar and its steps; when it has run, what still
 *              differs from Xero, each difference linked to its document, and
 *              any problem in words. Presentational: the parent owns the job
 *              (POST /api/statements/sync, followed with useJobStream) and
 *              hands in its state.
 *
 *              2.0.0: rebuilt slim on the new sync (refresh → one Xero read →
 *              credit allocations → comparison → the run's check), replacing
 *              the nine-step panel of the 1 Oct draft.
 *
 *              Props:
 *                job       { status, progress, result, error, attached, message }
 *                last      the last sync for this period (GET /sync/latest .last)
 *                onSync    start (or follow) a sync
 *                disabledReason  why the button is greyed (empty = enabled)
 */

import React, { useState } from 'react';
import './StatementSyncPanel.css';
import LoadingBar from './LoadingBar';
import EntityLink from './EntityLink';
import { useSlideOutButton } from './SlideOutButton';
import { isLiveJob, formatLondonWhen, docEntityType, plural } from '../utils/statementIntegrity';

const STEP_ICONS = {
  waiting: 'radio_button_unchecked',
  running: 'progress_activity',
  done: 'check_circle',
  error: 'error',
  skipped: 'block'
};

const SHOWN_DIFFERENCES = 50;

/** The result to show: the live job's, else the last saved one. */
function shownResult(job, last) {
  if (job?.result) return job.result;
  if (!last) return null;
  return {
    status: last.status,
    finishedAt: last.finished_at || last.syncedAt,
    steps: last.steps || [],
    reconciliation: last.reconciliation || null,
    check: last.check || null,
    problems: last.warning ? [{ step: '', message: last.warning }] : [],
    syncedByName: last.syncedByName || null
  };
}

function headlineOf(result) {
  const rec = result?.reconciliation;
  const n = rec?.differenceTotal ?? (Array.isArray(rec?.differences) ? rec.differences.length : 0);
  const mismatch = result?.check?.counts?.mismatch || 0;
  const parts = [];
  if (rec) parts.push(n ? `${plural(n, 'difference')} with Xero` : 'This system matches Xero for these contacts');
  else parts.push('Not compared with Xero');
  if (result?.check) parts.push(mismatch ? `${plural(mismatch, 'statement')} don’t match` : 'every statement checked');
  return parts.join(' · ');
}

function StatementSyncPanel({ job = null, last = null, onSync, disabledReason = '' }) {
  const [showAll, setShowAll] = useState(false);
  const running = isLiveJob(job?.status);
  useSlideOutButton([running, !!disabledReason]);
  const progress = job?.progress || null;
  const steps = (running ? progress?.steps : null) || shownResult(job, last)?.steps || [];
  const result = running ? null : shownResult(job, last);
  const differences = result?.reconciliation?.differences || [];
  const shown = showAll ? differences : differences.slice(0, SHOWN_DIFFERENCES);
  const problems = (result?.problems || []).filter((p) => p && p.message);
  const bad = !!result && ((result.reconciliation?.differenceTotal || 0) > 0 || (result.check?.counts?.mismatch || 0) > 0);

  return (
    <div className={`stmt-sync-panel${bad ? ' has-differences' : ''}`}>
      <div className="stmt-sync-head">
        <div className="stmt-sync-title">
          <span className="material-symbols-rounded">sync_saved_locally</span>
          <div>
            <div className="stmt-sync-name">Sync with Xero</div>
            <div className="stmt-sync-sub">
              {running
                ? (job?.attached ? 'Following the sync already running…' : 'Syncing…')
                : result?.finishedAt
                  ? `Last synced ${formatLondonWhen(result.finishedAt)}${result.syncedByName ? ` by ${result.syncedByName}` : ''} — ${headlineOf(result)}`
                  : 'Brings payments, documents and credit allocations in from Xero, then shows anything that still differs. Reads from Xero only.'}
            </div>
          </div>
        </div>
        <div className="buttons slideout">
          <div>
            <button
              type="button"
              className="btn-slideout"
              data-write-action
              onClick={onSync}
              disabled={running || !!disabledReason}
              title={disabledReason || (running ? 'A sync is running' : 'Bring this system up to date with Xero and compare')}
            >
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${running ? ' stmt-integrity-spin' : ''}`}>{running ? 'progress_activity' : 'sync'}</span>
              </div>
              <span className="btn-label">{running ? 'Syncing…' : 'Sync with Xero'}</span>
            </button>
          </div>
        </div>
      </div>

      {running && (
        <LoadingBar progress={progress?.percent || 0} message={progress?.message || 'Starting…'} size="sm" />
      )}

      {(running || (job?.result && steps.length > 0)) && (
        <ul className="stmt-sync-steps">
          {steps.map((s) => (
            <li key={s.key} className={`stmt-sync-step ${s.status}`}>
              <span className={`material-symbols-rounded${s.status === 'running' ? ' stmt-integrity-spin' : ''}`}>{STEP_ICONS[s.status] || STEP_ICONS.waiting}</span>
              <span className="stmt-sync-step-label">{s.label || s.key}</span>
              {s.message && <span className="stmt-sync-step-msg">{s.message}</span>}
            </li>
          ))}
        </ul>
      )}

      {job?.status === 'failed' || job?.status === 'lost' ? (
        <div className="stmt-sync-line bad">
          <span className="material-symbols-rounded">error</span>
          <span>{job.error || 'The sync failed'}</span>
        </div>
      ) : null}

      {!running && problems.length > 0 && (
        <ul className="stmt-sync-problems">
          {problems.map((p, i) => (
            <li key={i}><span className="material-symbols-rounded">warning</span>{p.message}</li>
          ))}
        </ul>
      )}

      {!running && differences.length > 0 && (
        <div className="stmt-sync-diffs">
          <div className="stmt-sync-diffs-head">
            <span>Still different from Xero ({result.reconciliation.differenceTotal ?? differences.length})</span>
            {differences.length > SHOWN_DIFFERENCES && (
              <button type="button" className="stmt-sync-toggle" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Show fewer' : `Show all ${differences.length}`}
              </button>
            )}
          </div>
          <ul>
            {shown.map((d, i) => (
              <li key={`${d.code}-${d.xeroId || d.localId || i}-${i}`}>
                <span className="stmt-sync-acc">{d.accountNo || d.companyName || ''}</span>
                {d.docNo && (
                  <EntityLink type={docEntityType(d.docType) || 'invoice'} id={d.localId}>{d.docNo}</EntityLink>
                )}
                <span className="stmt-sync-msg">{d.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default StatementSyncPanel;
